import React, { useContext } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import ThemeContext from "../../WebAppsSharedStuffs/context/ThemeProviderContext";
import { THEME_COLORS } from "../../WebAppsSharedStuffs/themes/DarkLight";
import { doc, increment, updateDoc, setDoc, getDoc } from "firebase/firestore";
import { db } from "../../backend/ArthereumProvider";

// Track navigation clicks for analytics
const trackNavClick = async (navName: string) => {
  try {
    const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD
    const analyticsRef = doc(db, "athAnalytics", `nav_clicks_${today}`);
    const docSnap = await getDoc(analyticsRef);
    
    if (docSnap.exists()) {
      await updateDoc(analyticsRef, {
        [`${navName.toLowerCase()}_tab_clicks`]: increment(1),
        lastUpdated: new Date()
      });
    } else {
      await setDoc(analyticsRef, {
        date: today,
        [`${navName.toLowerCase()}_tab_clicks`]: 1,
        lastUpdated: new Date()
      });
    }
  } catch (error) {
    console.log('Analytics tracking error:', error);
  }
};

interface NavItemProps {
  href: string;
  name: string;
  icon: React.ComponentType<{ className?: string; color?: string }>;
  isCentral?: boolean;
  isDesktop?: boolean;
}

const NavItem: React.FC<NavItemProps> = ({
  href,
  name,
  icon: Icon,
  isCentral = false,
  isDesktop = false,
}) => {
  const location = useLocation();
  const isActive = location.pathname === href;
  const { theme } = useContext(ThemeContext);

  // This part is for a sidebar, updated assuming a light theme sidebar
  if (isDesktop) {
    return (
      <Link
        style={{
          paddingBottom: "2px",
          color: isActive
            ? THEME_COLORS[theme as keyof typeof THEME_COLORS].blue
            : THEME_COLORS[theme as keyof typeof THEME_COLORS].gray,
        }}
        to={href}
        // REMOVED: hover:text-indigo-600 hover:bg-gray-100
        className="relative flex items-center justify-start w-full px-4 py-3 my-1 rounded-lg transition-colors duration-200"
      >
        <Icon
          className={`h-5 w-5`}
          color={
            isActive
              ? THEME_COLORS[theme as keyof typeof THEME_COLORS].blue
              : THEME_COLORS[theme as keyof typeof THEME_COLORS].gray
          }
        />
        <span
          className={`ml-4 text-sm font-medium ${
            isActive ? "font-semibold" : "" // Made active text bold
          }`}
          style={{
            color: isActive
              ? THEME_COLORS[theme as keyof typeof THEME_COLORS].blue
              : THEME_COLORS[theme as keyof typeof THEME_COLORS].gray,
          }}
        >
          {name}
        </span>
        {isActive && (
          <motion.div
            className="absolute left-0 w-1 h-full rounded-r-full"
            layoutId="active-pill"
            style={{
              backgroundColor:
                THEME_COLORS[theme as keyof typeof THEME_COLORS].blue,
            }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
          />
        )}
      </Link>
    );
  }

  const handleMobileNavClick = () => {
    trackNavClick(name); // Track the nav click for analytics
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  // The central button usually has a strong brand color, which works
  // on both light and dark backgrounds, so it remains unchanged.
  if (isCentral) {
    return (
      <Link to={href} className="relative" onClick={handleMobileNavClick}>
        <motion.div
          className="flex items-center justify-center w-16 h-16 rounded-full shadow-lg -translate-y-6 text-white"
          style={{
            backgroundColor:
              THEME_COLORS[theme as keyof typeof THEME_COLORS].blue,
          }}
          // REMOVED: whileHover={{ scale: 1.1, rotate: 15 }}
          whileTap={{ scale: 0.9 }}
          transition={{ type: "spring", stiffness: 300 }}
        >
          <Icon className="w-8 h-8" />
        </motion.div>
      </Link>
    );
  }

  // Mobile Nav Item
  return (
    <Link
      to={href}
      // REMOVED: hover:text-indigo-600
      className="relative flex flex-col items-center justify-center flex-1 transition-colors duration-200"
      style={{
        color: isActive
          ? THEME_COLORS[theme as keyof typeof THEME_COLORS].blue
          : THEME_COLORS[theme as keyof typeof THEME_COLORS].gray,
      }}
      onClick={handleMobileNavClick}
    >
      <Icon
        className={`w-5 h-5 mb-0.5 transition-colors duration-200`}
        color={
          isActive
            ? THEME_COLORS[theme as keyof typeof THEME_COLORS].blue
            : THEME_COLORS[theme as keyof typeof THEME_COLORS].gray
        }
      />
      <span
        className={`text-[11px] transition-colors duration-200`}
        style={{
          color: isActive
            ? THEME_COLORS[theme as keyof typeof THEME_COLORS].blue
            : THEME_COLORS[theme as keyof typeof THEME_COLORS].gray,
        }}
      >
        {name}
      </span>
      {isActive && (
        <motion.div
          layoutId="active-pill-mobile"
          className="absolute -bottom-1 w-6 h-0.5 rounded-full"
          style={{
            backgroundColor:
              THEME_COLORS[theme as keyof typeof THEME_COLORS].blue,
          }}
          transition={{ type: "spring", stiffness: 300, damping: 30 }}
        />
      )}
    </Link>
  );
};

export default NavItem;
